import { useEffect, useState } from "react";
import { BrandMark } from "../components/BrandMark";

/**
 * Sign-in as in the other OS products: email and password, and below it the
 * demo accounts, one click each. A private account is never listed; it signs
 * in with its email and password like anyone else.
 */

interface PersonaOption {
  key: string;
  label: string;
  name: string;
  blurb: string;
}

interface PersonaResponse {
  /** Absent on builds older than Phase 0; treated as enabled. */
  enabled?: boolean;
  gated: boolean;
  personas: PersonaOption[];
}

/** Where to land after signing in — honours ?next= / ?returnTo= if present. */
function returnTarget(): string {
  if (typeof window === "undefined") return "/post-login";
  const params = new URLSearchParams(window.location.search);
  return params.get("returnTo") ?? params.get("next") ?? "/post-login";
}

async function errorText(res: Response, fallback: string): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
  if (typeof body.detail === "string") return body.detail;
  if (body.error === "invalid_access_code") return "That access code was not accepted.";
  if (body.error === "demo_login_disabled") return "Demo accounts are switched off on this build.";
  return fallback;
}

export function LoginPage() {
  const [config, setConfig] = useState<PersonaResponse | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetch("/api/auth/personas", { credentials: "include" })
      .then((r) => (r.ok ? (r.json() as Promise<PersonaResponse>) : null))
      .catch(() => null)
      .then((d) => {
        if (live && d) setConfig(d);
      });
    return () => {
      live = false;
    };
  }, []);

  async function post(key: string, url: string, body: unknown) {
    setPending(key);
    setError(null);
    try {
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        setError(await errorText(res, "Sign-in failed. Please try again."));
        return;
      }
      window.location.assign(returnTarget());
    } catch {
      setError("Sign-in failed. Please try again.");
    } finally {
      setPending(null);
    }
  }

  const demoOn = config !== null && config.enabled !== false && config.personas.length > 0;

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <BrandMark height={28} />
        </div>
        <h1 className="login-title" data-testid="login-heading">
          Sign in
        </h1>

        <form
          className="login-form"
          onSubmit={(e) => {
            e.preventDefault();
            void post("password", "/api/auth/password-login", { email, password });
          }}
        >
          <label className="login-label">
            Email
            <input
              className="login-input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              required
            />
          </label>
          <label className="login-label">
            Password
            <span className="login-password">
              <input
                className="login-input"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                required
              />
              <button
                type="button"
                className="login-show"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="login-show-password"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </span>
          </label>

          {error ? (
            <p role="alert" className="login-error" data-testid="login-error">
              {error}
            </p>
          ) : null}

          <button type="submit" className="login-submit" disabled={pending !== null} data-testid="login-submit">
            {pending === "password" ? "Signing in…" : "Sign in"}
          </button>
        </form>

        {demoOn ? (
          <section className="login-demo" data-testid="demo-accounts">
            <div className="login-demo-head">
              <span>Demo accounts</span>
              <span className="login-demo-note">One click · synthetic data</span>
            </div>
            {config?.gated ? (
              <label className="login-label">
                Access code
                <input
                  id="access-code"
                  className="login-input"
                  type="password"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  data-testid="access-code"
                  autoComplete="off"
                  placeholder="From whoever gave you the link"
                />
              </label>
            ) : null}
            <div className="login-demo-list">
              {config?.personas.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className="login-persona"
                  onClick={() => void post(p.key, "/api/auth/login", { persona: p.key, accessCode: accessCode || undefined })}
                  disabled={pending !== null}
                  data-testid={`persona-${p.key}`}
                >
                  <span className="login-persona-role">{p.label}</span>
                  <span className="login-persona-name">{pending === p.key ? "Signing in…" : p.name}</span>
                  <span className="login-persona-blurb">{p.blurb}</span>
                </button>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
